/** Common error-body shape from the insurance API. */
interface ApiErrorBody {
  detail?: string;
  title?: string;
  message?: string;
}

/**
 * Pull a message out of an error `data` body (string body or
 * `{ detail|title|message }`). Empty strings don't count — an object body with
 * nothing human-readable must fall through to the caller's fallback, never be
 * String()-ed at users.
 */
export function bodyMessage(data: unknown): string | undefined {
  if (typeof data === 'string') return data || undefined;
  if (data && typeof data === 'object') {
    const { detail, title, message } = data as ApiErrorBody;
    for (const candidate of [detail, title, message]) {
      if (typeof candidate === 'string' && candidate) return candidate;
    }
  }
  return undefined;
}

/**
 * Best-effort user-facing message from anything thrown — the single body-aware
 * extractor behind `getErrorMessage` and `responseStatusHandler`. Prefer the
 * server body's `detail`/`title`/`message` (axios `error.response.data` or
 * RTK Query `error.data`), then a string transport error, then `Error#message`,
 * else the fallback.
 */
export function mapErrorMessage(error: unknown, fallback = 'Something went wrong'): string {
  if (typeof error === 'string') return error || fallback;
  if (!error || typeof error !== 'object') return fallback;

  const axiosShape = error as { response?: { data?: unknown } };
  const fbq = error as { data?: unknown; error?: unknown; message?: unknown };

  return (
    bodyMessage(axiosShape.response?.data) ??
    bodyMessage(fbq.data) ??
    // Transport error string, then Error#message — bodyMessage doubles as the
    // non-empty-string filter, and its detail→title order encodes the priority.
    bodyMessage({ detail: fbq.error, title: fbq.message }) ??
    fallback
  );
}
